import type { ReactNode } from 'react';

import {
  getAtPath,
  renameInstrument,
  renamePattern,
  SOUND_KINDS,
  type DocPath,
  type SoundDoc,
} from '../../lib/sound-doc.js';
import { selectionPathOf, useSound, type SoundSelection } from '../../state/sound.js';
import { Card, JsonField, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { EffectsEditor } from './EffectsEditor.js';
import { InstrumentEditor } from './InstrumentEditor.js';
import { LayerInspector } from './LayerInspector.js';
import { NotesTable } from './NotesTable.js';
import { TextArea, VoiceEditor } from './VoiceEditor.js';

/**
 * Right-hand inspector for the Sound tab's selection. Layers, instruments,
 * the voice and effect chains use the shared editors (LayerInspector,
 * InstrumentEditor, VoiceEditor, EffectsEditor); tracks, clips, patterns,
 * voice lines, master and document settings have compact forms here. Every
 * change goes through the store (undo, autosave, re-render).
 */

function set(path: DocPath, value: unknown): void {
  useSound.getState().setPath(path, value);
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card title={title}>
      <div className="snd-insp-body">{children}</div>
    </Card>
  );
}

function DocSettings({ doc }: { doc: SoundDoc }) {
  const song = doc.kind === 'song' || !!doc.tracks?.length;
  const exp = doc.export ?? {};
  return (
    <>
      <Section title="Sound">
        <PropRow label="Name">
          <TextField value={doc.name} onChange={(v) => v.trim() && set(['name'], v.trim())} />
        </PropRow>
        <PropRow label="Kind">
          <SelectField value={doc.kind} options={SOUND_KINDS} onChange={(v) => useSound.getState().setKind(v as SoundDoc['kind'])} />
        </PropRow>
        <PropRow label="Duration" hint="Seconds; empty = until the tails decay below -90 dB">
          <NumberField value={num(doc.duration)} step={0.01} min={0} onChange={(v) => set(['duration'], v > 0 ? v : undefined)} />
        </PropRow>
        <PropRow label="Seed" hint="Every random source is seeded">
          <NumberField value={num(doc.seed)} integer step={1} onChange={(v) => set(['seed'], Math.round(v))} />
        </PropRow>
      </Section>
      {song && (
        <Section title="Song">
          <PropRow label="Tempo" hint="BPM (quarter notes)">
            <NumberField value={num(doc.tempo) ?? 120} step={1} min={20} max={400} onChange={(v) => set(['tempo'], v)} />
          </PropRow>
          <PropRow label="Key" hint='e.g. "A minor", "F# dorian"'>
            <TextField value={doc.key} placeholder="C major" onChange={(v) => set(['key'], v.trim() || undefined)} />
          </PropRow>
          <PropRow label="Swing" hint="0..1">
            <NumberField value={num(doc.swing) ?? 0} step={0.05} min={0} max={1} onChange={(v) => set(['swing'], v || undefined)} />
          </PropRow>
        </Section>
      )}
      <Section title="Export">
        <PropRow label="Path">
          <TextField value={exp.path} mono placeholder={`audio/${doc.name || 'sound'}.wav`} onChange={(v) => set(['export', 'path'], v.trim() || undefined)} />
        </PropRow>
        <PropRow label="Bit depth">
          <SelectField value={String(exp.bitDepth ?? 24)} options={['16', '24', '32']} onChange={(v) => set(['export', 'bitDepth'], Number(v))} />
        </PropRow>
        <PropRow label="Channels">
          <SelectField value={String(exp.channels ?? 2)} options={[{ value: '1', label: 'Mono' }, { value: '2', label: 'Stereo' }]} onChange={(v) => set(['export', 'channels'], Number(v))} />
        </PropRow>
        <PropRow label="Normalize" hint="Target peak dBFS (empty = off)">
          <NumberField value={num(exp.normalize)} step={0.1} max={0} onChange={(v) => set(['export', 'normalize'], v)} />
        </PropRow>
        <PropRow label="Trim silence">
          <Toggle checked={!!exp.trim} onChange={(v) => set(['export', 'trim'], v || undefined)} />
        </PropRow>
        <PropRow label="Tail" hint="Seconds kept after the last audible sample">
          <NumberField value={num(exp.tail)} step={0.01} min={0} onChange={(v) => set(['export', 'tail'], v)} />
        </PropRow>
        <PropRow label="Loop" hint='{ "start": s, "end": s, "crossfade": s }'>
          <JsonField value={exp.loop} rows={2} onChange={(v) => set(['export', 'loop'], v ?? undefined)} />
        </PropRow>
      </Section>
      <Section title="Variables & variants">
        <PropRow label="Variables" hint='Named numbers used as "$name" in any numeric field'>
          <JsonField value={doc.variables} rows={3} onChange={(v) => set(['variables'], v ?? undefined)} />
        </PropRow>
        <PropRow label="Variants" hint='[{ "name": "step_2", "variables": {...}, "seed": 2 }]'>
          <JsonField value={doc.variants} rows={3} onChange={(v) => set(['variants'], v ?? undefined)} />
        </PropRow>
      </Section>
      <MasterSettings doc={doc} />
    </>
  );
}

function MasterSettings({ doc }: { doc: SoundDoc }) {
  const m = doc.master ?? {};
  return (
    <>
      <Section title="Master">
        <PropRow label="Gain" hint="dB">
          <NumberField value={num(m.gain) ?? 0} step={0.5} onChange={(v) => set(['master', 'gain'], v || undefined)} />
        </PropRow>
        <PropRow label="Limiter" hint="-1 dBTP true-peak limiter">
          <Toggle checked={m.limiter !== false} onChange={(v) => set(['master', 'limiter'], v)} />
        </PropRow>
        <PropRow label="Loudness" hint="Target LUFS (-16 game SFX, -14 music); empty = off">
          <NumberField value={num(m.loudness)} step={0.5} max={0} onChange={(v) => set(['master', 'loudness'], v)} />
        </PropRow>
      </Section>
      <Section title="Master effects">
        <EffectsEditor value={m.effects} doc={doc} onChange={(v) => set(['master', 'effects'], v)} />
      </Section>
      <Section title="Return buses">
        {Object.keys(m.returns ?? {}).map((name) => (
          <button key={name} type="button" className="ghost small snd-insp-link" onClick={() => useSound.getState().select({ kind: 'return', name })}>
            {name} ({m.returns![name]!.effects?.length ?? 0} effects)
          </button>
        ))}
        <button
          type="button"
          className="ghost small"
          onClick={() => {
            const names = Object.keys(m.returns ?? {});
            let name = 'reverb';
            for (let i = 2; names.includes(name); i++) name = `reverb_${i}`;
            set(['master', 'returns', name], { effects: [{ type: 'reverb', size: 0.7, decay: 2 }] });
            useSound.getState().select({ kind: 'return', name });
          }}
        >
          + Add return bus
        </button>
      </Section>
    </>
  );
}

function TrackInspector({ doc, index }: { doc: SoundDoc; index: number }) {
  const t = doc.tracks?.[index];
  if (!t) return null;
  const base: DocPath = ['tracks', index];
  const instruments = Object.keys(doc.instruments ?? {});
  return (
    <>
      <Section title="Track">
        <PropRow label="Name">
          <TextField value={t.name} onChange={(v) => set([...base, 'name'], v.trim() || undefined)} />
        </PropRow>
        <PropRow label="Instrument" hint="Name in instruments (or a *.sound.json path)">
          <SelectField value={typeof t.instrument === 'string' ? t.instrument : undefined} options={instruments} onChange={(v) => set([...base, 'instrument'], v)} />
        </PropRow>
        <PropRow label="Gain" hint="dB">
          <NumberField value={num(t.gain) ?? 0} step={0.5} onChange={(v) => set([...base, 'gain'], v)} />
        </PropRow>
        <PropRow label="Pan" hint="-1 left … 1 right">
          <NumberField value={num(t.pan) ?? 0} step={0.05} min={-1} max={1} onChange={(v) => set([...base, 'pan'], v || undefined)} />
        </PropRow>
        <PropRow label="Mute">
          <Toggle checked={!!t.mute} onChange={(v) => set([...base, 'mute'], v || undefined)} />
        </PropRow>
        <PropRow label="Sends" hint='{ "reverb": -12 } (dB to master.returns)'>
          <JsonField value={t.send} rows={2} onChange={(v) => set([...base, 'send'], v ?? undefined)} />
        </PropRow>
        <PropRow label="Automation" hint='{ "gain": { "env": [[0, -60], [4, -6]] } } — env times in bars'>
          <JsonField value={t.automation} rows={3} onChange={(v) => set([...base, 'automation'], v ?? undefined)} />
        </PropRow>
      </Section>
      <Section title="Track effects">
        <EffectsEditor value={t.effects} doc={doc} onChange={(v) => set([...base, 'effects'], v)} />
      </Section>
      {typeof t.instrument === 'string' && doc.instruments?.[t.instrument] && (
        <button type="button" className="ghost small snd-insp-link" onClick={() => useSound.getState().select({ kind: 'instrument', name: t.instrument as string })}>
          Edit instrument “{t.instrument}”
        </button>
      )}
    </>
  );
}

function ClipInspector({ doc, track, clip }: { doc: SoundDoc; track: number; clip: number }) {
  const c = doc.tracks?.[track]?.clips?.[clip];
  if (!c) return null;
  const base: DocPath = ['tracks', track, 'clips', clip];
  return (
    <Section title={`Clip on ${doc.tracks?.[track]?.name ?? `track ${track + 1}`}`}>
      <PropRow label="Pattern">
        <SelectField value={typeof c.pattern === 'string' ? c.pattern : undefined} options={Object.keys(doc.patterns ?? {})} onChange={(v) => set([...base, 'pattern'], v)} />
      </PropRow>
      <PropRow label="Start" hint='Bars from 0, or "bar.beat" (beat from 1)'>
        <TextField
          value={String(c.start ?? 0)}
          mono
          onChange={(v) => {
            const text = v.trim();
            if (/^\d+$/.test(text)) set([...base, 'start'], Number(text));
            else if (/^\d+\.[1-9]\d*(\.[1-9]\d*)?$/.test(text)) set([...base, 'start'], text);
          }}
        />
      </PropRow>
      <PropRow label="Repeat">
        <NumberField value={num(c.repeat) ?? 1} integer step={1} min={1} onChange={(v) => set([...base, 'repeat'], v > 1 ? Math.round(v) : undefined)} />
      </PropRow>
      <PropRow label="Transpose" hint="Semitones">
        <NumberField value={num(c.transpose) ?? 0} integer step={1} onChange={(v) => set([...base, 'transpose'], v || undefined)} />
      </PropRow>
      <PropRow label="Velocity" hint="Multiplier">
        <NumberField value={num(c.velocity) ?? 1} step={0.05} min={0} onChange={(v) => set([...base, 'velocity'], v === 1 ? undefined : v)} />
      </PropRow>
      <PropRow label="Length" hint="Beats (overrides the pattern length)">
        <NumberField value={num(c.length)} step={1} min={0} onChange={(v) => set([...base, 'length'], v > 0 ? v : undefined)} />
      </PropRow>
      {typeof c.pattern === 'string' && (
        <button type="button" className="ghost small snd-insp-link" onClick={() => useSound.getState().openPattern(c.pattern)}>
          Edit pattern “{c.pattern}”
        </button>
      )}
    </Section>
  );
}

function PatternInspector({ doc, name }: { doc: SoundDoc; name: string }) {
  const p = doc.patterns?.[name];
  if (!p) return null;
  const base: DocPath = ['patterns', name];
  return (
    <>
      <Section title="Pattern">
        <PropRow label="Name">
          <TextField
            value={name}
            onChange={(v) => {
              const next = v.trim();
              if (!next || next === name || doc.patterns?.[next]) return;
              useSound.getState().update(renamePattern(doc, name, next), { kind: 'pattern', name: next });
              useSound.getState().openPattern(next, useSound.getState().view);
            }}
          />
        </PropRow>
        <PropRow label="Length" hint="Beats (empty = content rounded up to bars)">
          <NumberField value={num(p.length)} step={1} min={0} onChange={(v) => set([...base, 'length'], v > 0 ? v : undefined)} />
        </PropRow>
        <PropRow label="Scale" hint='Overrides the song key, e.g. "A minor"'>
          <TextField value={p.scale} onChange={(v) => set([...base, 'scale'], v.trim() || undefined)} />
        </PropRow>
        <PropRow label="Steps/bar" hint="Drum steps per bar (default 4 per beat)">
          <NumberField value={num(p.steps)} integer step={1} min={1} onChange={(v) => set([...base, 'steps'], v >= 1 ? Math.round(v) : undefined)} />
        </PropRow>
        <PropRow label="Swing">
          <NumberField value={num(p.swing)} step={0.05} min={0} max={1} onChange={(v) => set([...base, 'swing'], v)} />
        </PropRow>
      </Section>
      <Section title={`Notes (${p.notes?.length ?? 0})`}>
        <NotesTable notes={p.notes ?? []} onChange={(notes) => set([...base, 'notes'], notes.length ? notes : undefined)} lyric={(p.notes ?? []).some((n) => n.lyric !== undefined)} />
      </Section>
      <Section title="Drum lanes">
        <JsonField value={p.drums} rows={4} onChange={(v) => set([...base, 'drums'], v ?? undefined)} />
      </Section>
    </>
  );
}

function VoiceLineInspector({ doc, index }: { doc: SoundDoc; index: number }) {
  const line = doc.voice?.lines?.[index];
  if (!line) return null;
  const base: DocPath = ['voice', 'lines', index];
  const field = (key: string, label: string, hint: string, step = 0.05, min?: number) => (
    <PropRow label={label} hint={hint}>
      <NumberField value={num(line[key])} step={step} min={min} onChange={(v) => set([...base, key], v)} />
    </PropRow>
  );
  return (
    <Section title={`Voice line ${index + 1}`}>
      <PropRow label="Text">
        <TextArea value={line.text ?? ''} rows={3} onChange={(v) => set([...base, 'text'], v)} />
      </PropRow>
      {field('start', 'Start', 'Seconds (empty = after the previous line)', 0.01, 0)}
      {field('speed', 'Speed', 'Length scale (1 = normal)', 0.05, 0.1)}
      {field('pitch', 'Pitch', 'Semitones (formant-preserving)', 0.5)}
      {field('energy', 'Energy', '1 = normal', 0.05, 0)}
      {field('noise', 'Variation', 'VITS noise scale', 0.05, 0)}
      {field('pause', 'Pause', 'Seconds after the line', 0.05, 0)}
      <PropRow label="Phonemes" hint="Explicit phonemes to fix pronunciation">
        <TextField value={typeof line.phonemes === 'string' ? line.phonemes : undefined} mono onChange={(v) => set([...base, 'phonemes'], v.trim() || undefined)} />
      </PropRow>
      <button type="button" className="ghost small snd-insp-link" onClick={() => useSound.getState().select({ kind: 'voice' })}>
        Edit the whole voice (voice model, effects, preview)
      </button>
    </Section>
  );
}

function ReturnInspector({ doc, name }: { doc: SoundDoc; name: string }) {
  const r = doc.master?.returns?.[name];
  if (!r) return null;
  const base: DocPath = ['master', 'returns', name];
  return (
    <>
      <Section title={`Return “${name}”`}>
        <PropRow label="Gain" hint="dB">
          <NumberField value={num(r.gain) ?? 0} step={0.5} onChange={(v) => set([...base, 'gain'], v || undefined)} />
        </PropRow>
      </Section>
      <Section title="Effects">
        <EffectsEditor value={r.effects} doc={doc} onChange={(v) => set([...base, 'effects'], v)} />
      </Section>
    </>
  );
}

function InstrumentInspector({ doc, name }: { doc: SoundDoc; name: string }) {
  const path: DocPath = name === '' ? ['instrument'] : ['instruments', name];
  return (
    <>
      {name !== '' && (
        <Section title="Instrument">
          <PropRow label="Name">
            <TextField
              value={name}
              onChange={(v) => {
                const next = v.trim();
                if (!next || next === name || doc.instruments?.[next]) return;
                useSound.getState().update(renameInstrument(doc, name, next), { kind: 'instrument', name: next });
              }}
            />
          </PropRow>
        </Section>
      )}
      <InstrumentEditor value={getAtPath(doc, path)} doc={doc} onChange={(v) => set(path, v)} />
    </>
  );
}

function title(sel: SoundSelection | null): string {
  if (!sel) return 'Document';
  switch (sel.kind) {
    case 'layer':
      return `Layer ${sel.index + 1}`;
    case 'track':
      return `Track ${sel.index + 1}`;
    case 'clip':
      return 'Clip';
    case 'pattern':
      return `Pattern ${sel.name}`;
    case 'instrument':
      return sel.name ? `Instrument ${sel.name}` : 'Instrument (this document)';
    case 'voice':
      return 'Voice';
    case 'voiceLine':
      return `Voice line ${sel.index + 1}`;
    case 'master':
      return 'Master';
    case 'return':
      return `Return ${sel.name}`;
    default:
      return 'Document';
  }
}

export function SoundInspector() {
  const doc = useSound((s) => s.doc);
  const sel = useSound((s) => s.selection);
  if (!doc) return <div className="pane-empty muted">No sound open</div>;
  const path = selectionPathOf(sel);
  let body: ReactNode;
  switch (sel?.kind) {
    case 'layer':
      body = <LayerInspector value={doc.layers?.[sel.index]} doc={doc} onChange={(v) => set(['layers', sel.index], v)} />;
      break;
    case 'track':
      body = <TrackInspector doc={doc} index={sel.index} />;
      break;
    case 'clip':
      body = <ClipInspector doc={doc} track={sel.track} clip={sel.clip} />;
      break;
    case 'pattern':
      body = <PatternInspector doc={doc} name={sel.name} />;
      break;
    case 'instrument':
      body = <InstrumentInspector doc={doc} name={sel.name} />;
      break;
    case 'voice':
      body = <VoiceEditor value={doc.voice} doc={doc} onChange={(v) => set(['voice'], v)} />;
      break;
    case 'voiceLine':
      body = <VoiceLineInspector doc={doc} index={sel.index} />;
      break;
    case 'master':
      body = <MasterSettings doc={doc} />;
      break;
    case 'return':
      body = <ReturnInspector doc={doc} name={sel.name} />;
      break;
    default:
      body = <DocSettings doc={doc} />;
  }
  return (
    <div className="snd-inspector">
      <div className="snd-insp-title">
        <strong>{title(sel)}</strong>
        {path && <span className="mono muted">{path.join('.')}</span>}
        {sel && (
          <button type="button" className="ghost small" onClick={() => useSound.getState().select(null)} title="Show document settings">
            Document
          </button>
        )}
      </div>
      {body}
    </div>
  );
}
